import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import LockOutlined from '@mui/icons-material/LockOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import VerifiedOutlined from '@mui/icons-material/VerifiedOutlined';
import { Helmet } from 'react-helmet-async';

import PageHeader from './PageHeader';
import { SITE_URL } from '../../lib/seo';
import { tArray } from '../../lib/i18nSafe';

const ICONS = {
  lock: LockOutlined,
  shield: ShieldOutlined,
  gavel: GavelOutlined,
  verified: VerifiedOutlined
};

export default function Trust() {
  const { t } = useTranslation();

  const badges = tArray(t, 'trust.badges');
  const pillars = tArray(t, 'trust.pillars');
  const subProcessors = tArray(t, 'trust.subProcessors');
  const modelCards = tArray(t, 'trust.modelCards');
  const policies = tArray(t, 'trust.policies');

  return (
    <Box>
      <Helmet>
        <title>Trust &amp; Security | Al-Amen Technology</title>
        <link rel="canonical" href={`${SITE_URL}/trust`} />
      </Helmet>
      <PageHeader
        eyebrow={t('trust.eyebrow')}
        title={t('trust.title')}
        body={t('trust.body')}
      />

      {/* Badges */}
      <Box component="section" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            {badges.map((b) => (
              <Grid key={b.title} item xs={6} sm={4} md={3}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    height: '100%',
                    bgcolor: 'background.paper'
                  }}
                >
                  <VerifiedOutlined sx={{ color: 'primary.main', mb: 1 }} />
                  <Typography sx={{ fontFamily: 'var(--font-serif)', fontSize: '1.3rem', mb: 0.5 }}>
                    {b.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {b.body}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pillars */}
      <Box
        component="section"
        id="responsible-ai"
        sx={{
          py: { xs: 6, md: 9 },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 3, md: 6 }}>
            <Grid item xs={12} md={4}>
              <Typography
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'text.secondary',
                  mb: 1.5
                }}
              >
                {t('trust.pillarsEyebrow')}
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: { xs: '2rem', md: '2.6rem' },
                  lineHeight: 1.08,
                  letterSpacing: '-0.02em'
                }}
              >
                {t('trust.pillarsTitle')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Stack spacing={0}>
                {pillars.map((p, idx) => {
                  const Icon = ICONS[p.icon] || ShieldOutlined;
                  return (
                    <Box
                      key={p.title}
                      sx={{
                        py: 3,
                        borderTop: idx === 0 ? '1px solid' : 'none',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Icon sx={{ color: 'primary.main', mt: 0.4 }} />
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: 'var(--font-serif)',
                              fontSize: '1.4rem',
                              lineHeight: 1.2,
                              mb: 0.75
                            }}
                          >
                            {p.title}
                          </Typography>
                          <Typography sx={{ color: 'text.secondary' }}>{p.body}</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Sub-processors table */}
      <Box component="section" id="sub-processors" sx={{ py: { xs: 6, md: 9 } }}>
        <Container maxWidth="xl">
          <Typography
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              color: 'text.secondary',
              mb: 1.5
            }}
          >
            {t('trust.subProcessorsEyebrow')}
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'var(--font-serif)',
              fontSize: { xs: '2rem', md: '2.6rem' },
              lineHeight: 1.08,
              letterSpacing: '-0.02em',
              mb: 3
            }}
          >
            {t('trust.subProcessorsTitle')}
          </Typography>

          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                    {t('trust.table.name')}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                    {t('trust.table.purpose')}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                    {t('trust.table.region')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subProcessors.map((sp) => (
                  <TableRow key={sp.name}>
                    <TableCell sx={{ fontWeight: 600 }}>{sp.name}</TableCell>
                    <TableCell>{sp.purpose}</TableCell>
                    <TableCell sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: 'text.secondary' }}>
                      {sp.region}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Container>
      </Box>

      {/* Model cards */}
      <Box
        component="section"
        id="model-cards"
        sx={{
          py: { xs: 6, md: 9 },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Container maxWidth="xl">
          <Typography
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              color: 'text.secondary',
              mb: 1.5
            }}
          >
            {t('trust.modelCardsEyebrow')}
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'var(--font-serif)',
              fontSize: { xs: '2rem', md: '2.6rem' },
              lineHeight: 1.08,
              letterSpacing: '-0.02em',
              mb: 3
            }}
          >
            {t('trust.modelCardsTitle')}
          </Typography>
          <Grid container spacing={3}>
            {modelCards.map((m) => (
              <Grid key={m.name} item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{ p: 3, borderRadius: 3, height: '100%', bgcolor: 'background.default' }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Chip label={m.version} size="small" />
                    <Typography
                      sx={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '0.75rem',
                        color: 'text.secondary'
                      }}
                    >
                      {m.released}
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{ fontFamily: 'var(--font-serif)', fontSize: '1.6rem', letterSpacing: '-0.015em', mb: 1 }}
                  >
                    {m.name}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', mb: 2 }}>{m.body}</Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {(m.tags || []).map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Policies */}
      <Box component="section" id="privacy" sx={{ py: { xs: 6, md: 9 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            {policies.map((p) => (
              <Grid key={p.title} item xs={12} sm={6} md={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    height: '100%',
                    bgcolor: 'background.paper',
                    transition: 'border-color 180ms ease',
                    '&:hover': { borderColor: 'primary.main' }
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.72rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.14em',
                      color: 'text.secondary',
                      mb: 1
                    }}
                  >
                    {p.type}
                  </Typography>
                  <Typography
                    sx={{ fontFamily: 'var(--font-serif)', fontSize: '1.4rem', mb: 1, letterSpacing: '-0.015em' }}
                  >
                    {p.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {p.body}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
